import { useState } from 'react';
import { useCurrentUser } from '@lp/core/hooks/useCurrentUser';
import { useChatFiltersStore } from '@features/store/chat-filters.store';
import { ChatFilterState, UseChatFiltersReturn } from '@features/components/Chat/types';
import { DEFAULT_CHAT_FILTER_STATE } from '@features/components/Chat/constants';
import { OrganType, DonorType } from '@/api/apis/case';
import { UserChatFilters, UserPreferenceType } from '@/api/apis/user';
import { StreamChannelType } from '@/api/apis/chat';

export const keyMap: Record<keyof ChatFilterState, UserChatFilters> = {
  organTypes: UserChatFilters.chatOrganTypes,
  donorTypes: UserChatFilters.chatDonorTypes,
  chatTypes: UserChatFilters.chatChatTypes,
  unreadOnly: UserChatFilters.chatUnreadOnly,
};

export function useChatFilters(): UseChatFiltersReturn {
  const { setFilters, ...stored } = useChatFiltersStore();
  const { updateUserPreferences } = useCurrentUser();

  const [draftFilters, setDraft] = useState<ChatFilterState>(stored ?? { ...DEFAULT_CHAT_FILTER_STATE });
  const [version, setVersion] = useState(0);

  const setOrganTypes = (v: OrganType[]) => setDraft((p) => ({ ...p, organTypes: v }));
  const setDonorTypes = (v: DonorType[]) => setDraft((p) => ({ ...p, donorTypes: v }));
  const setChatTypes = (v: StreamChannelType[]) => setDraft((p) => ({ ...p, chatTypes: v }));
  const toggleUnreadOnly = () => setDraft((p) => ({ ...p, unreadOnly: !p.unreadOnly }));

  const persistToPrefs = (filters: ChatFilterState) => {
    const prefs: UserPreferenceType[] = Object.entries(filters).reduce<UserPreferenceType[]>((acc, [key, val]) => {
      const prefName = keyMap[key as keyof ChatFilterState];
      if (prefName) acc.push({ name: prefName, value: JSON.stringify(val) });
      return acc;
    }, []);

    updateUserPreferences(prefs);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setVersion((v) => v + 1);
    persistToPrefs(draftFilters);
  };

  const clearAll = () => {
    const empty = { ...DEFAULT_CHAT_FILTER_STATE };
    setDraft(empty);
    setFilters(empty);
    setVersion((v) => v + 1);
  };

  const setAndApplyFilter = <K extends keyof ChatFilterState>(key: K, value: ChatFilterState[K]) => {
    const newDraft = { ...draftFilters, [key]: value };
    setDraft(newDraft);
    setFilters(newDraft);
    setVersion((ver) => ver + 1);
    persistToPrefs(newDraft);
  };

  return {
    draftFilters,
    filters: stored,
    version,
    setOrganTypes,
    setDonorTypes,
    setChatTypes,
    toggleUnreadOnly,
    applyFilters,
    clearAll,
    setAndApplyFilter,
  };
}
